import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Image from 'next/image';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { Eye, ChevronLeft, ChevronRight, Download, BookOpen, Sparkles } from 'lucide-react';
import PDFBookViewer from '@/components/book-samples/PDFBookViewer';

export interface Style {
  id: string;
  name: string;
  description: string;
  previewImageUrl: string;
  isActive: boolean;
  experimental?: boolean;
}

interface StyleSelectionStepProps {
  styles: Style[];
  selectedStyleId?: string;
  onStyleSelect: (styleId: string) => void;
  error?: string;
}

interface SampleBook {
  id: string;
  title: string;
  characterName: string;
  style: string;
  theme: string;
  pageCount: number;
  coverUrl: string;
  pdfUrl?: string;
  heroPhoto?: string;
}

export function StyleSelectionStep({
  styles,
  selectedStyleId,
  onStyleSelect,
  error
}: StyleSelectionStepProps) {
  // DEBUG: Verify this component is using updated code
  console.log('StyleSelectionStep mounted - VERSION: INLINE_STYLES_FIX_V2');
  
  const [hoveredStyle, setHoveredStyle] = useState<string | null>(null);
  const [sampleBook, setSampleBook] = useState<SampleBook | null>(null);
  const [isLoadingSample, setIsLoadingSample] = useState(false);
  const [currentBook, setCurrentBook] = useState(0);
  const [sampleBooks, setSampleBooks] = useState<SampleBook[]>([]);
  
  // Fetch sample when style is selected
  useEffect(() => {
    if (!selectedStyleId) {
      setSampleBook(null);
      setSampleBooks([]);
      return;
    }
    
    const selectedStyle = styles.find(s => s.id === selectedStyleId);
    if (!selectedStyle) return;
    
    setIsLoadingSample(true);
    
    // Fetch sample books for the selected style
    fetch(`/api/sample-books?style=${encodeURIComponent(selectedStyle.name)}&limit=5`)
      .then(res => res.json())
      .then(data => {
        if (data.samples && data.samples.length > 0) {
          console.log(`📚 Found ${data.samples.length} samples for style "${selectedStyle.name}"`);
          
          // Filter to only include books with PDFs
          const booksWithPdfs = data.samples.filter((book: SampleBook) => book.pdfUrl);
          console.log(`📚 ${booksWithPdfs.length} samples have PDFs`);
          
          if (booksWithPdfs.length > 0) {
            setSampleBooks(booksWithPdfs);
            setSampleBook(booksWithPdfs[0]);
            setCurrentBook(0);
            
            // Log which book we're showing
            console.log(`📚 Showing sample: "${booksWithPdfs[0].title}" (${booksWithPdfs[0].style})`);
          } else {
            // Fallback if no PDFs available
            console.warn(`📚 No PDF samples found for style "${selectedStyle.name}"`);
            setSampleBooks([]);
            setSampleBook(null);
          }
        } else {
          console.warn(`📚 No samples found for style "${selectedStyle.name}"`);
          setSampleBooks([]);
          setSampleBook(null);
        }
      })
      .catch(err => {
        console.error('Failed to fetch sample:', err);
        setSampleBooks([]);
        setSampleBook(null);
      })
      .finally(() => {
        setIsLoadingSample(false);
      });
  }, [selectedStyleId, styles]);
  
  // Update current sample book when navigating
  useEffect(() => {
    if (sampleBooks.length > 0 && currentBook < sampleBooks.length) {
      setSampleBook(sampleBooks[currentBook]);
    }
  }, [currentBook, sampleBooks]);
  
  const handleBookChange = (direction: 'prev' | 'next') => {
    if (sampleBooks.length <= 1) return;
    
    setCurrentBook(prev => {
      if (direction === 'prev') {
        return (prev - 1 + sampleBooks.length) % sampleBooks.length;
      } else {
        return (prev + 1) % sampleBooks.length;
      }
    });
  };
  
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {styles.map((style) => {
          const isSelected = selectedStyleId === style.id;
          const isHovered = hoveredStyle === style.id;
          
          return (
            <div key={style.id} className="flex flex-col">
              <motion.button
                type="button"
                onClick={() => onStyleSelect(style.id)}
                onMouseEnter={() => setHoveredStyle(style.id)}
                onMouseLeave={() => setHoveredStyle(null)}
                className={cn(
                  'relative group overflow-hidden rounded-xl transition-all',
                  'border-2 focus:outline-none focus:ring-4 focus:ring-offset-2',
                  'min-h-[120px] sm:min-h-[140px]', // Ensure good touch target
                  isSelected ? [
                    'border-purple-600 shadow-xl',
                    'focus:ring-purple-500'
                  ] : [
                    'border-gray-200 hover:border-gray-300 shadow-md',
                    'focus:ring-gray-400'
                  ]
                )}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
              >
                {/* Style preview image */}
                <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
                  <Image
                    src={style.previewImageUrl}
                    alt={style.name}
                    fill
                    className="object-cover"
                    sizes="(max-width: 768px) 50vw, 33vw"
                  />
                </div>
                
                {/* Selected indicator */}
                {isSelected && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute top-2 right-2 w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center shadow-lg"
                  >
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </motion.div>
                )}
                
                {/* New style badge */}
                {style.experimental && (
                  <div className="absolute top-2 left-2 bg-gradient-to-r from-blue-500 to-purple-500 text-xs font-bold px-2 py-1 rounded-full text-white shadow-md">
                    New
                  </div>
                )}
              </motion.button>
              
              {/* Style name - always visible below the image */}
              <h3 className="mt-2 text-center font-bold text-sm text-gray-900">
                {style.name}
              </h3>
            </div>
          );
        })}
      </div>
      
      {error && (
        <p className="text-sm text-red-500 mt-2">{error}</p>
      )}
      
      {/* Professional Sample Preview Section using PDFBookViewer */}
      {selectedStyleId && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="mt-8 bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-3 sm:p-6 border border-purple-100 -mx-4 sm:mx-0"
        >
          {/* Header */}
          <div className="text-center mb-6 bg-white/95 backdrop-blur-sm p-6 rounded-xl shadow-sm" style={{ backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
            <div className="flex items-center justify-center gap-2 mb-3">
              <Sparkles className="w-6 h-6 text-purple-600" />
              <h3 className="text-xl md:text-2xl font-bold" style={{ color: '#1f2937' }}>
                Preview {styles.find(s => s.id === selectedStyleId)?.name} Style
              </h3>
              <Sparkles className="w-6 h-6 text-purple-600" />
            </div>
            <p className="text-sm md:text-base max-w-2xl mx-auto" style={{ color: '#4b5563' }}>
              See how your child's book will look with this artistic style. 
              Browse through complete sample books below.
            </p>
          </div>
          
          {isLoadingSample ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          ) : sampleBook && sampleBook.pdfUrl ? (
            <div>
              {/* Book selector if multiple samples */}
              {sampleBooks.length > 1 && (
                <div className="flex items-center justify-center gap-4 mb-4">
                  <button
                    onClick={() => handleBookChange('prev')}
                    className="p-2 rounded-full bg-purple-100 hover:bg-purple-200 transition"
                  >
                    <ChevronLeft className="w-4 h-4 text-purple-700" />
                  </button>
                  
                  <div className="text-center min-w-0 flex-1 max-w-md">
                    <h4 className="text-base font-bold text-gray-800 truncate">{sampleBook.title}</h4>
                    <p className="text-xs text-gray-600">
                      Sample {currentBook + 1} of {sampleBooks.length} • {sampleBook.characterName}'s Adventure
                    </p>
                  </div>
                  
                  <button
                    onClick={() => handleBookChange('next')}
                    className="p-2 rounded-full bg-purple-100 hover:bg-purple-200 transition"
                  >
                    <ChevronRight className="w-4 h-4 text-purple-700" />
                  </button>
                </div>
              )}

              {/* Professional PDF Viewer */}
              <div className="bg-white rounded-xl p-0 sm:p-4 shadow-lg -mx-3 sm:mx-0">
                <PDFBookViewer
                  pdfUrl={sampleBook.pdfUrl}
                  bookTitle={sampleBook.title}
                  enableAutoFlip={true}
                  autoFlipInterval={5000}
                  maxWidth={1200}
                  className="mb-4"
                />
                
                {/* Quality note */}
                <div className="mt-4 p-4 bg-purple-50 rounded-lg">
                  <div className="flex items-start gap-3">
                    <BookOpen className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-purple-700">
                      <p className="font-semibold mb-1">Professional Quality</p>
                      <p>
                        This is the exact quality and style your personalized book will have. 
                        Each page will feature your child as the hero in the {styles.find(s => s.id === selectedStyleId)?.name} artistic style.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Download link if available */}
                {sampleBook.pdfUrl && (
                  <div className="mt-4 text-center">
                    <a
                      href={sampleBook.pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm text-purple-600 hover:text-purple-700"
                    >
                      <Download className="w-4 h-4" />
                      Download Sample PDF
                    </a>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl p-8 text-center">
              <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">
                {isLoadingSample 
                  ? "Loading sample books..." 
                  : "No sample books available for this style yet."}
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Your book will still be beautifully created in the {styles.find(s => s.id === selectedStyleId)?.name} style.
              </p>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}
